import React from 'react';
import {
  MobileHeader, SectionNav, StatTiles, Card, EmptyState, ErrorState, Button,
} from '@parsina/framework';
import { UserX, Pill } from 'lucide-react';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { usePatient } from '../../hooks/usePatient.js';
import { PATIENT_SECTIONS } from './PatientDetail.shared.js';
import MobileAllergyStrip from '../../components/mobile/MobileAllergyStrip.jsx';
import MobilePatientSummary from '../../components/mobile/MobilePatientSummary.jsx';
import MobilePatientHistory from '../../components/mobile/MobilePatientHistory.jsx';
import MobilePatientChart from '../../components/mobile/MobilePatientChart.jsx';
import PlanTab from './tabs/PlanTab.jsx';
import { PatientDetailSkeletonMobile } from './PatientDetail.skeleton.jsx';

// پرونده — THE SECTIONED MOBILE RECORD (DESIGN-CONCEPT-MOBILE §4C).
//
// ┌──────────────────────────────────────┐
// │ ① MobileHeader — CONTENT mode         │  ← back · name · کد ملی · file no.
// │    action: «نسخه» (the route exists)  │  scrolls away
// │ ② StatTiles 4-up — riding the edge    │  سن · گروه خونی · حساسیت · دارو
// │ ③ MobileAllergyStrip — STICKY         │  an allergy must never scroll off
// │ ④ SectionNav — STICKY                 │  خلاصه · چارت دندان · سوابق
// │ ⑤ the active section                  │  ONE page scroll under the sticky block
// └──────────────────────────────────────┘
//
// NO BOTTOM NAV (§4C): this is a drilled-into full-screen surface, so the
// header's content mode carries the way back. A bar offering «بیماران» here
// would be an escape hatch out of a record the dentist is halfway through
// charting.
//
// ③ AND ④ ARE THE ONLY STICKY CHROME. The header and the identity glance scroll
// away — the record wins the viewport — but a critical allergy stays visible at
// every scroll position and a section is always one tap away.
//
// Sections are CONTENT, not screens: each is one `Mobile*` component rendering
// into the same body, driven by the `?tab=` route segment through
// `PATIENT_SECTIONS`. An unknown segment falls back to خلاصه — the container
// (index.jsx) owns that rule for both surfaces.

const PatientDetailMobile = ({ id, tab, setTab, goBack }) => {
  const { t, formatNumber, formatDate } = useTranslation();
  const navigate = useAppNavigate();
  const { activeId } = useClinicScope();
  const { patient, loading, error, reload } = usePatient(id, activeId);

  const sections = React.useMemo(() => PATIENT_SECTIONS(t), [t]);
  const navItems = React.useMemo(
    () => sections.map(({ value, label, Icon }) => ({
      key: value,
      label,
      icon: <Icon size={16} aria-hidden />,
    })),
    [sections],
  );

  // The record's OWN mobile shape — the gradient band, the 4-up tiles riding
  // its edge, the section navigator and the body of the section the URL
  // names — never a generic card stack (DESIGN.md §"Loading & skeleton").
  if (loading && !patient) {
    return <PatientDetailSkeletonMobile tab={tab} label={t('common.loading')} />;
  }

  // A failed read and a record that does not exist are different facts: only one
  // of them is worth a retry button.
  if (error && !patient) {
    return (
      <div data-testid="patient-content" data-state="error" role="alert" style={{ padding: '0.875rem 0.75rem' }}>
        <ErrorState
          title={t('patient.error.title')}
          description={t('errors.load.patient')}
          onRetry={reload}
          retryLabel={t('common.actions.retry')}
        />
      </div>
    );
  }

  if (!patient) {
    return (
      <div data-testid="patient-content" data-state="empty" style={{ padding: '0.875rem 0.75rem' }}>
        <Card>
          <EmptyState
            icon={<UserX size={44} color={C.textMuted} />}
            title={t('patient.notFound.title')}
            description={t('patient.notFound.description')}
            action={
              <Button variant="outline" className="min-h-11" onClick={goBack}>
                {t('common.actions.back')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const allergies = patient.allergies || [];
  const identity = [
    patient.nationalId || null,
    patient.fileNumber ? `${t('common.labels.fileNumber')} ${patient.fileNumber}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // Values PRE-FORMATTED (§8). A missing datum renders «—» inside the kit rather
  // than dropping its tile, so the row is always 4-up (§4).
  const tiles = [
    {
      key: 'age',
      label: t('patient.stats.age'),
      value: patient.age != null ? formatNumber(patient.age) : null,
      solid: true,
    },
    { key: 'blood', label: t('patient.fields.bloodType'), value: patient.bloodType || null },
    {
      key: 'allergies',
      label: t('patient.stats.allergies'),
      value: formatNumber(allergies.length),
      // The one number on this row that changes what may be prescribed on the
      // next screen, so it is the one that is allowed to be red.
      color: allergies.length > 0 ? C.red : undefined,
    },
    {
      key: 'meds',
      label: t('patient.stats.medications'),
      value: formatNumber((patient.medications || []).length),
    },
  ];

  return (
    // ONE scroll region, and the header and the identity glance are INSIDE it:
    // §4C — "The header and the identity glance scroll away — the record wins
    // the viewport — but a critical allergy stays visible at every scroll
    // position, and section switching is always one tap away."
    <div
      data-testid="patient-content"
      data-state="content"
      style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
    >
      <MobileHeader
        onBack={goBack}
        backLabel={t('common.actions.back')}
        title={patient.name}
        subtitle={identity || undefined}
        data-testid="patient-header"
        actions={[
          // §3.2: an action appears only when its destination exists. نسخه does
          // (`/prescription/:patientId`), so this one resolves.
          {
            key: 'prescribe',
            icon: <Pill size={18} aria-hidden />,
            label: t('patient.actions.prescribe'),
            onClick: () => navigate('prescription', { patientId: patient.id }),
          },
        ]}
      />

      {/* ② rides the header's bottom edge — the elevated/overlapping language
          §2.2 allows mobile and not the workstation. */}
      <div style={{ padding: '0 0.75rem', marginTop: '-1rem', position: 'relative' }}>
        <StatTiles tiles={tiles} data-testid="patient-stats" />
      </div>

      {/* ③④ THE ONLY STICKY CHROME, as ONE block.
          Sticking them separately means guessing the strip's height to offset
          the navigator by — and that height depends on how many allergens wrap
          onto a second line. One sticky container makes the two move together
          whatever the strip turns out to be. */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 3,
          background: C.bg,
          marginTop: '0.75rem',
        }}
      >
        <MobileAllergyStrip allergies={allergies} />
        <SectionNav
          items={navItems}
          active={tab}
          onSelect={setTab}
          ariaLabel={t('patient.tabs.sectionsLabel')}
          data-testid="patient-nav"
        />
      </div>

      {/* ⑤ The kit's SectionNav declares role="tablist"/role="tab"; this is the
          other half of the pattern. `aria-label` rather than `aria-labelledby` —
          the kit generates no ids. */}
      <div
        role="tabpanel"
        aria-label={sections.find((s) => s.value === tab)?.label}
        style={{ padding: '0.875rem 0.75rem 1.5rem' }}
      >
        {tab === 'chart' ? (
          <MobilePatientChart patientId={id} clinicId={activeId} />
        ) : tab === 'plan' ? (
          // Shared with the desktop rather than reduced: the plan is a list of
          // short rows, which is the one shape that needs no second layout.
          <PlanTab patientId={id} clinicId={activeId} t={t} formatNumber={formatNumber} formatDate={formatDate} />
        ) : tab === 'history' ? (
          <MobilePatientHistory patient={patient} />
        ) : (
          <MobilePatientSummary patient={patient} />
        )}
      </div>
    </div>
  );
};

export default PatientDetailMobile;
